/**
 * Cliente HTTP tipado.
 *
 * - Injeta `Authorization: Bearer <jwt>` se o auth store estiver autenticado.
 * - Gera `X-Request-ID` por chamada (correlação backend/frontend; padrão
 *   do legado mantido).
 * - Parse de body via zod schema obrigatório — refuse silent any.
 * - **401 → refresh-then-retry (TD26-31)**: chama `refreshAccessToken()`
 *   single-flight no store, e se sucesso re-executa o request UMA vez com
 *   o novo bearer. Se o refresh falhar (refresh token expirado/inválido),
 *   o store já chamou `logout()` e propagamos o ApiError 401 original.
 *   Flag `_retried` no objeto local previne loop refresh→401→refresh.
 *
 * Dois entrypoints:
 * - `apiFetch<T>(opts)` retorna `Promise<T>` — compat com chamadores antigos.
 * - `apiFetchWithMeta<T>(opts)` retorna `Promise<{data: T, meta: ResponseMeta}>`
 *   — capturam `X-Data-Source`/`X-Data-Error`/`X-Cache-Age-S` pra observability
 *   visual (source pill + freshness pill — TD27-07/08).
 */

import type { z } from 'zod';
import { getAccessToken, useAuthStore } from './auth-store';
import { env } from './env';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly requestId: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Metadados de observability extraídos dos response headers.
 *
 * Backend implementa a convenção do napkin `v2-api-graceful-degradation-headers`:
 * - `X-Data-Source: postgres-hot | postgres-cold | empty | env | redis | …`
 * - `X-Data-Error: 1` (presente quando houve degradação)
 * - `X-Cache-Age-S: <int>` (presente em respostas hot — idade do snapshot Redis)
 *
 * `fetchedAt` é injetado client-side: ms epoch quando o response aterrissou.
 * Source pill + freshness pill usam isso.
 */
export type ResponseMeta = {
  /** Valor cru de `X-Data-Source` (ex: "postgres-hot", "postgres-cold", "empty", "env"). null se ausente. */
  dataSource: string | null;
  /** `X-Data-Error: 1` → true. Default false. */
  dataError: boolean;
  /** Idade do cache em segundos (X-Cache-Age-S). null se ausente. */
  cacheAgeS: number | null;
  /** ms epoch client-side quando o response aterrissou — pra freshness "atualizado há Xs". */
  fetchedAt: number;
};

function extractMeta(response: Response): ResponseMeta {
  const cacheAgeRaw = response.headers.get('x-cache-age-s');
  const cacheAgeS = cacheAgeRaw != null ? Number.parseInt(cacheAgeRaw, 10) : null;
  return {
    dataSource: response.headers.get('x-data-source'),
    dataError: response.headers.get('x-data-error') === '1',
    cacheAgeS: cacheAgeS != null && Number.isFinite(cacheAgeS) ? cacheAgeS : null,
    fetchedAt: Date.now(),
  };
}

function generateRequestId(scope: string): string {
  // crypto.randomUUID() em browsers modernos. Fallback Math.random caso polyfill ausente.
  const id =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  return `${scope}-${id}`;
}

export type ApiOptions<T> = {
  /** Path absoluto sem o host (`/api/v2/...`) */
  path: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  /** Schema zod p/ validar e tipar o response body */
  schema: z.ZodType<T>;
  /** Scope curto pro X-Request-ID (default: 'api') */
  scope?: string;
  /** Headers extras (Bearer já é injetado se autenticado) */
  headers?: Record<string, string>;
  /** AbortSignal (do TanStack Query, p.ex.) */
  signal?: AbortSignal;
  /** Pular auth header mesmo se logado (útil pra /login) */
  skipAuth?: boolean;
};

/**
 * Variante que retorna `{data, meta}` — captura headers de observability.
 * Use em queries que alimentam SourcePill/FreshnessPill (snapshot, stats, facets).
 */
export async function apiFetchWithMeta<T>(
  opts: ApiOptions<T>,
): Promise<{ data: T; meta: ResponseMeta }> {
  return _apiFetchWithMetaInternal(opts, false);
}

async function _apiFetchWithMetaInternal<T>(
  opts: ApiOptions<T>,
  retried: boolean,
): Promise<{ data: T; meta: ResponseMeta }> {
  const requestId = generateRequestId(opts.scope ?? 'api');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Request-ID': requestId,
    ...(opts.headers ?? {}),
  };

  if (!opts.skipAuth) {
    const token = getAccessToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  const url = `${env.API_BASE_URL}${opts.path}`;

  let response: Response;
  try {
    response = await fetch(url, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
      credentials: 'omit', // Bearer em header — não usamos cookies
    });
  } catch (cause) {
    // Network error / DNS / CORS preflight failed
    throw new ApiError(
      `Falha de rede: ${cause instanceof Error ? cause.message : 'unknown'}`,
      0,
      requestId,
    );
  }

  // 401 (TD26-31): tenta refresh single-flight + retry UMA vez. Se refresh
  // falhar, store já chamou logout — propaga o 401 pro chamador.
  // `skipAuth` requests (login, refresh) NÃO entram nesse fluxo.
  if (response.status === 401 && !opts.skipAuth && !retried) {
    const ok = await useAuthStore.getState().refreshAccessToken();
    if (ok) {
      return _apiFetchWithMetaInternal(opts, true);
    }
    // ok=false → store já fez logout. Cai pro path normal de ApiError abaixo.
  }

  let parsedBody: unknown;
  try {
    parsedBody = await response.json();
  } catch {
    parsedBody = undefined;
  }

  if (!response.ok) {
    const message =
      (parsedBody && typeof parsedBody === 'object' && 'message' in parsedBody
        ? String((parsedBody as { message: unknown }).message)
        : null) ?? `HTTP ${response.status}`;
    throw new ApiError(message, response.status, requestId, parsedBody);
  }

  const result = opts.schema.safeParse(parsedBody);
  if (!result.success) {
    throw new ApiError(
      `Resposta inválida do servidor: ${result.error.message}`,
      response.status,
      requestId,
      parsedBody,
    );
  }
  return { data: result.data, meta: extractMeta(response) };
}

/**
 * Variante compatível — retorna só o body parseado (ignora headers).
 * Mantida pra chamadores que não precisam de meta (auth, etc).
 */
export async function apiFetch<T>(opts: ApiOptions<T>): Promise<T> {
  const { data } = await apiFetchWithMeta(opts);
  return data;
}
